import { createContext, useState, useEffect, useContext, useCallback, useMemo } from "react";
import translations from "../translations";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem("nexusflow-lang") || "en";
  });

  useEffect(() => {
    const isArabic = lang === "ar";
    document.documentElement.dir = isArabic ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    document.body.style.fontFamily = isArabic
      ? "'Tajawal', 'IBM Plex Arabic', 'Cairo', sans-serif"
      : "'DM Sans', 'Inter', sans-serif";
  }, [lang]);

  const setLang = useCallback((newLang) => {
    localStorage.setItem("nexusflow-lang", newLang);
    setLangState(newLang);
  }, []);

  const toggleLang = useCallback(() => {
    setLang(lang === "en" ? "ar" : "en");
  }, [lang, setLang]);

  const t = useCallback(
    (key) => {
      const dict = translations[lang] || translations["en"];
      return dict[key] !== undefined ? dict[key] : key;
    },
    [lang]
  );

  const isRTL = useMemo(() => lang === "ar", [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggleLang, t, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;